import { sort } from "@prismicio/editor-support/Array"
import { isHTMLElement } from "@prismicio/editor-support/DOM"
import { some } from "@prismicio/editor-support/Map"
import {
  useForceUpdate,
  useStableCallback,
} from "@prismicio/editor-support/React"
import {
  type AnimationEvent,
  type CSSProperties,
  type RefObject,
  useLayoutEffect,
  useRef,
} from "react"

interface UseLayoutParams<ITEM> {
  containerRef: RefObject<HTMLUListElement>
  items: readonly ITEM[]
  getItemKey: (item: ITEM) => string
}

export function useLayout<ITEM>(params: UseLayoutParams<ITEM>) {
  const { containerRef, items, getItemKey } = params

  // Use a mutable Map for convenience/perfs but keep a version around for precise change detection.
  const layout = useRef<Layout<ITEM>>({ version: 0, items: new Map() }).current

  const forceUpdate = useForceUpdate()

  function onLayoutChanged() {
    layout.version++
    forceUpdate()
  }

  const previousVersion = layout.version

  updateLayoutFromItems({
    items,
    getItemKey,
    layout,
  })

  // Prepare for a potential move animation and update the latest
  // children offsets before rendering (and thus, before they potentially move).
  if (containerRef.current && previousVersion !== layout.version)
    updateOffsets({ container: containerRef.current, layout })

  useLayoutEffect(() => {
    if (!containerRef.current) return

    maybeAnimateMoves({
      container: containerRef.current,
      layout,
      forceUpdate,
    })
  }, [layout, layout.version, forceUpdate, containerRef])

  const onAnimationEnd = useStableCallback(
    (evt: AnimationEvent<HTMLElement>) => {
      // Reject unrelated bubbling events (e.g, Animations on nested elements)
      if (evt.target !== evt.currentTarget) return

      const key = getKey(evt.currentTarget)
      if (key === undefined) return

      const itemState = layout.items.get(key)
      if (!itemState) return

      if (itemState.state === "entering") {
        itemState.state = "present"
      } else if (itemState.state === "exiting") {
        layout.items.delete(key)
        onLayoutChanged()
      }
    },
  )

  // The layout.items map is sorted by insertion order: re-order to match the new items ordering.
  const itemStates = sort([...layout.items.values()], (s) => s.index)

  return {
    itemStates,
    onAnimationEnd,
  }
}

function updateLayoutFromItems<ITEM>(args: {
  items: readonly ITEM[]
  getItemKey: (item: ITEM) => string
  layout: Layout<ITEM>
}): void {
  const { items, getItemKey, layout } = args

  const isFirstRender = layout.version === 0

  const itemsWithKeys = items.map((item, index) => ({
    item,
    index,
    key: String(getItemKey(item)),
  }))

  const keySet = new Set(itemsWithKeys.map((s) => s.key))
  const exitingItems: State<ITEM>[] = []
  const previousIndex = new Map<State<ITEM>, number>()
  let changed = false

  // Skip mounting/enter animations for already present items.
  // In general, another animation would happen higher up, if any.
  if (isFirstRender) {
    changed = true
    itemsWithKeys.forEach(({ item, index, key }) => {
      layout.items.set(key, { state: "present", item, index, key })
    })
  }

  // Entering, updating and moving items.
  itemsWithKeys.forEach(({ item, index, key }) => {
    const itemState = layout.items.get(key)

    // Entered.
    if (!itemState) {
      layout.items.set(key, { state: "entering", item, index, key })
      changed = true
      return
    }

    // Keep data in sync.
    itemState.item = item

    // Moved.
    if (itemState.index !== index) {
      previousIndex.set(itemState, itemState.index)
      itemState.index = index
      changed = true
    }
  })

  // Exiting items.
  layout.items.forEach((itemState) => {
    if (keySet.has(itemState.key)) return

    itemState.state = "exiting"
    changed = true
    exitingItems.push(itemState)
  })

  // If there are both entering and exiting items, delay the entering ones
  // to make it easier on the eye.
  if (exitingItems.length)
    layout.items.forEach((itemState) => {
      if (itemState.state === "entering") itemState.state = "hidden"
    })
  else
    layout.items.forEach((itemState) => {
      if (itemState.state === "hidden") itemState.state = "entering"
    })

  // If we have exits, we need to offset existing items to account for the soon to be removed items.
  if (exitingItems.length)
    layout.items.forEach((itemState) => {
      if (itemState.state !== "present") return

      const itemsExitingBefore = exitingItems.filter(
        (item) => item.index < (previousIndex.get(itemState) ?? -1),
      ).length

      itemState.index += itemsExitingBefore
    })

  if (changed) layout.version++
}

function maybeAnimateMoves<ITEM>(params: {
  container: HTMLUListElement
  layout: Layout<ITEM>
  forceUpdate: () => void
}) {
  const { container, layout, forceUpdate } = params

  // Give priority to exit animations.
  if (some(layout.items, (s) => s.state === "exiting")) return

  const { changed } = computeMoveLayout({
    container,
    layout,
  })

  if (!changed) return

  requestAnimationFrame(() => {
    // 2) Now remove the styles so that the items snap back to their new natural position.
    layout.items.forEach((itemState) => {
      itemState.style = {}
    })
    forceUpdate()
  })

  // 1) The layout has moves: synchronously flush the new styles.
  forceUpdate()
}

function computeMoveLayout<ITEM>(params: {
  container: HTMLElement
  layout: Layout<ITEM>
}): { changed: boolean } {
  const { container, layout } = params

  let changed = false

  for (const li of container.children) {
    const element = getMainElement(li)
    if (!element) continue

    const key = getKey(element)
    if (key === undefined) continue

    const itemState = layout.items.get(key)
    if (!itemState) continue

    const previousOffset = itemState.offset
    const offset = { left: element.offsetLeft, top: element.offsetTop }

    // If it's a new item we don't know about or an item that
    // is still animating in/out: nothing to do but remember its current offsets.
    if (!previousOffset || itemState.state !== "present") {
      layout.items.set(key, { ...itemState, offset })
      continue
    }

    // This item did not move: Skip.
    if (
      offset.left === previousOffset.left &&
      offset.top === previousOffset.top
    ) {
      continue
    }

    // This item did move.
    changed = true
    const deltaX = previousOffset.left - offset.left
    const deltaY = previousOffset.top - offset.top

    layout.items.set(key, {
      ...itemState,
      offset,
      // Immediately transforms the item back to its old position.
      style: {
        transform: `translate(${deltaX}px, ${deltaY}px)`,
        transitionDuration: "0ms",
      },
    })
  }

  // If we schedule any move transitions, delay impacted entering items:
  // we want the moves to happen first so it looks cleaner.
  if (changed) {
    layout.items.forEach((itemState) => {
      if (itemState.state !== "entering") return
      if (itemState.index === layout.items.size - 1) return
      itemState.animationDelay = true
    })
  }

  return { changed }
}

function updateOffsets<ITEM>(params: {
  container: HTMLElement
  layout: Layout<ITEM>
}): void {
  const { container, layout } = params

  for (const li of container.children) {
    const element = getMainElement(li)
    if (!element) continue

    const key = getKey(element)
    if (key === undefined) continue

    const itemState = layout.items.get(key)
    if (!itemState) continue

    const offset = { left: element.offsetLeft, top: element.offsetTop }
    itemState.offset = offset
  }
}

interface Layout<ITEM> {
  items: Map<string, State<ITEM>>
  version: number
}

export interface State<ITEM> {
  state: "hidden" | "entering" | "present" | "exiting"
  index: number
  item: ITEM
  key: string
  animationDelay?: boolean
  offset?: { top: number; left: number }
  style?: CSSProperties
}

const dataKey = "data-animatedlist-key"

export function getMainElement(from: Element): HTMLElement | undefined {
  const element = from.querySelector(`[${dataKey}]`)
  if (!isHTMLElement(element)) return
  return element
}

export function getKey(element: Element) {
  const key = element.getAttribute(dataKey)
  return key ?? undefined
}

export function getMainDataProps<ITEM>(itemState: State<ITEM>) {
  return { [dataKey]: itemState.key }
}
